import { useEffect, useState } from "react";
import { create } from "zustand";
import { useQueryClient } from "@tanstack/react-query";
import { getPrefs, useSavePrefs } from "../lib/prefs";
import type { AppConfig } from "../lib/config";
import { cn } from "../lib/cn";
import { Dialog } from "./ui/Dialog";
import { Button, IconButton } from "./ui/Button";
import { toast } from "./ui/Toast";
import { IconCopy, IconDanger, IconGlobe, IconLock, IconShieldCheck, IconShieldWarning } from "./icons";
import { copyText } from "../features/posts/usePostActions";

/*
 * Trust & safety: every link that leaves Atrium goes through this modal first,
 * showing exactly where it leads and flagging common tricks. Domains can be
 * trusted (synced in preferences) and the check can be turned off in settings.
 */

const SHORTENERS = new Set(["bit.ly", "t.co", "tinyurl.com", "goo.gl", "ow.ly", "is.gd", "buff.ly", "rebrand.ly", "cutt.ly", "shorturl.at", "rb.gy", "tiny.cc", "lnkd.in"]);

/** Registered domain, roughly: the last two labels, or three for common second-level suffixes. */
export function baseDomain(host: string) {
  const parts = host.replace(/^www\./, "").split(".");
  const twoLevel = /^(co|com|org|net|gov|ac|edu)\.[a-z]{2}$/.test(parts.slice(-2).join("."));
  return parts.slice(twoLevel ? -3 : -2).join(".");
}

export function isTrusted(host: string, trusted: string[]) {
  const h = host.toLowerCase();
  return trusted.some((d) => h === d || h.endsWith(`.${d}`));
}

/**
 * Atrium's own sites never need a warning: every host on the app's registered
 * domain (useatrium.app and its subdomains: portal, docs, API) plus the surfaces
 * the server reports, which covers *.localhost in development.
 */
function isOwnSite(host: string, config: AppConfig | undefined) {
  const here = window.location.hostname;
  const ip = /^\d{1,3}(\.\d{1,3}){3}$/.test(here) || here.startsWith("[");
  const own = [ip ? here : baseDomain(here)];
  for (const origin of [config?.developerPortalUrl, config?.docsUrl]) {
    try {
      if (origin) own.push(new URL(origin).hostname);
    } catch {
      /* not a URL */
    }
  }
  return isTrusted(host, own);
}

interface Warning {
  tone: "danger" | "warning";
  text: string;
}

function analyse(url: URL, linkText: string | null): Warning[] {
  const out: Warning[] = [];
  const host = url.hostname;
  if (url.username || url.password) out.push({ tone: "danger", text: "The address hides its real destination behind a user name. It actually goes to " + host + "." });
  if (host.includes("xn--") || /[^\x00-\x7f]/.test(host)) out.push({ tone: "danger", text: "The address uses unusual characters that can imitate a well-known site." });
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.startsWith("[")) out.push({ tone: "warning", text: "The link goes to a raw IP address rather than a named site." });
  if (url.protocol === "http:") out.push({ tone: "warning", text: "The site doesn't use a secure connection. Don't enter passwords or payment details." });
  if (SHORTENERS.has(host.replace(/^www\./, ""))) out.push({ tone: "warning", text: "This is a shortened link, so the final destination is hidden." });
  if (linkText) {
    const shown = /^(?:https?:\/\/)?([a-z0-9-]+(?:\.[a-z0-9-]+)+)/i.exec(linkText.trim())?.[1]?.toLowerCase();
    if (shown && baseDomain(shown) !== baseDomain(host)) out.push({ tone: "danger", text: `The link text shows ${shown}, but it goes to ${host}.` });
  }
  return out;
}

interface GuardState {
  url: URL | null;
  text: string | null;
  open: (url: URL, text: string | null) => void;
  close: () => void;
}
const useGuard = create<GuardState>((set) => ({
  url: null,
  text: null,
  open: (url, text) => set({ url, text }),
  close: () => set({ url: null, text: null }),
}));

/** Open an external URL through the guard (for buttons that aren't <a> elements). */
export function openExternal(href: string, text: string | null = null) {
  try {
    useGuard.getState().open(new URL(href, window.location.href), text);
  } catch {
    toast.error("That link isn't valid");
  }
}

function visit(url: URL) {
  window.open(url.toString(), "_blank", "noopener,noreferrer");
}

/** Mount once near the root. Intercepts clicks on external links app-wide. */
export function LinkGuardHost() {
  const qc = useQueryClient();
  const savePrefs = useSavePrefs();
  const { url, text, open, close } = useGuard();
  const [trust, setTrust] = useState(false);
  useEffect(() => setTrust(false), [url]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || (e.type === "auxclick" && e.button !== 1)) return;
      const a = (e.target as HTMLElement | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a || a.dataset.linkGuard === "off" || a.hasAttribute("download")) return;
      let u: URL;
      try {
        u = new URL(a.getAttribute("href")!, window.location.href);
      } catch {
        return;
      }
      if (!/^https?:$/.test(u.protocol) || u.origin === window.location.origin) return;
      const prefs = getPrefs(qc);
      if (!prefs.linkWarnings || isOwnSite(u.hostname, qc.getQueryData<AppConfig>(["config"])) || isTrusted(u.hostname, prefs.trustedDomains)) {
        // Trusted: still never leak the opener.
        if (a.target === "_blank" && !a.rel.includes("noopener")) a.rel = `${a.rel} noopener noreferrer`.trim();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const label = a.textContent?.trim() || null;
      open(u, label && label !== a.href ? label : null);
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("auxclick", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("auxclick", onClick, true);
    };
  }, [qc, open]);

  const warnings = url ? analyse(url, text) : [];
  const danger = warnings.some((w) => w.tone === "danger");
  const domain = url ? baseDomain(url.hostname) : "";
  const signedIn = !!qc.getQueryData(["me"]);

  const go = () => {
    if (!url) return;
    if (trust && signedIn) {
      const current = getPrefs(qc).trustedDomains;
      if (!current.includes(domain)) void savePrefs({ trustedDomains: [...current, domain].slice(-200) });
    }
    visit(url);
    close();
  };

  const rest = url ? `${url.pathname === "/" ? "" : url.pathname}${url.search}${url.hash}` : "";
  const sub = url ? url.hostname.slice(0, Math.max(0, url.hostname.length - domain.length)) : "";

  return (
    <Dialog
      open={!!url}
      onOpenChange={(o) => !o && close()}
      title={
        <span className="flex flex-col gap-3">
          <span className={cn("flex size-10 items-center justify-center rounded-xl", danger ? "bg-danger-soft text-danger" : warnings.length ? "bg-surface-2 text-warning" : "bg-surface-2 text-fg-2")}>
            {warnings.length ? <IconShieldWarning className="icon size-5" aria-hidden /> : <IconShieldCheck className="icon size-5" aria-hidden />}
          </span>
          {danger ? "This link looks suspicious" : "You're leaving Atrium"}
        </span>
      }
      description="Check the address before you continue. Atrium doesn't control external sites."
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={close} data-autofocus>
            Go back
          </Button>
          <Button variant={danger ? "danger" : "primary"} onClick={go}>
            {danger ? "Visit anyway" : "Visit site"}
          </Button>
        </>
      }
    >
      {url && (
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg border border-line bg-surface px-3 py-2.5">
            {url.protocol === "https:" ? (
              <IconLock className="icon mt-0.5 size-4 shrink-0 text-fg-3" aria-label="Secure connection" />
            ) : (
              <IconGlobe className="icon mt-0.5 size-4 shrink-0 text-warning" aria-label="Not secure" />
            )}
            <p className="min-w-0 flex-1 break-all font-mono text-xs leading-5 text-fg-3">
              {url.protocol}//
              {url.username && <span className="text-danger">{url.username}@</span>}
              <span className="text-fg-2">{sub}</span>
              <span className="font-semibold text-fg">{domain}</span>
              {url.port && `:${url.port}`}
              <span className="line-clamp-3">{rest}</span>
            </p>
            <IconButton
              size="sm"
              label="Copy link"
              icon={IconCopy}
              className="-my-1 -mr-1"
              onClick={() => {
                void copyText(url.toString());
                toast.show("Link copied");
              }}
            />
          </div>
          {warnings.length > 0 && (
            <ul className="space-y-2">
              {warnings.map((w, i) => (
                <li key={i} className={cn("flex gap-2 text-sm", w.tone === "danger" ? "text-danger" : "text-fg-2")}>
                  <IconDanger className={cn("icon mt-0.5 size-4 shrink-0", w.tone === "danger" ? "text-danger" : "text-warning")} aria-hidden />
                  {w.text}
                </li>
              ))}
            </ul>
          )}
          {signedIn && !danger && (
            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-fg-2">
              <input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} className="size-4 accent-[var(--accent)]" />
              Trust <span className="font-medium text-fg">{domain}</span> and don't ask again
            </label>
          )}
        </div>
      )}
    </Dialog>
  );
}
